export type SkillId =
  | 'python'
  | 'javascript'
  | 'typescript'
  | 'react'
  | 'nextjs'
  | 'nodejs'
  | 'mongodb'
  | 'docker'
  | 'azure'
  | 'aws'
  | 'llms'
  | 'ml'
  | 'rag'
  | 'langchain'
  | 'ai'
  | 'frontend'
  | 'backend'
  | 'cloud'
  | 'database'
  | 'three'
  | 'architecture';

export interface SkillCoreDef {
  id: SkillId;
  title: string;
  move: string;
  skills: string[];
  color: string;
  /** Rating shown on FUT-style pack */
  ovr: number;
  /** World Z position along the lane (negative forward). */
  z: number;
}

export const SKILL_CORES: SkillCoreDef[] = [
  {
    id: 'python',
    title: 'Python Core',
    move: 'Quick Sidestep',
    skills: ['Automation', 'Backend Development', 'AI Agents', 'Python Ecosystem'],
    color: '#3776ab',
    ovr: 91,
    z: -12,
  },
  {
    id: 'javascript',
    title: 'JavaScript Core',
    move: 'Faster Touch',
    skills: ['Interactive Apps', 'Modern Frontend', 'Event Loops', 'DX'],
    color: '#f7df1e',
    ovr: 88,
    z: -22,
  },
  {
    id: 'typescript',
    title: 'TypeScript Core',
    move: 'Precision Window',
    skills: ['Type Safety', 'Enterprise Patterns', 'Contracts', 'DX'],
    color: '#3178c6',
    ovr: 90,
    z: -32,
  },
  {
    id: 'react',
    title: 'React Core',
    move: 'Dash Sprint',
    skills: [
      'Component Architecture',
      'Interactive UI',
      'Performance',
      'Animations',
    ],
    color: '#61dafb',
    ovr: 92,
    z: -42,
  },
  {
    id: 'nextjs',
    title: 'Next.js Core',
    move: 'Route Break',
    skills: ['SSR / SSG', 'App Router', 'Edge', 'Full-Stack React'],
    color: '#f5f7fa',
    ovr: 89,
    z: -52,
  },
  {
    id: 'nodejs',
    title: 'Node.js Core',
    move: 'Power-Through',
    skills: ['REST APIs', 'Express', 'Auth', 'Realtime'],
    color: '#68a063',
    ovr: 88,
    z: -62,
  },
  {
    id: 'mongodb',
    title: 'MongoDB Core',
    move: 'Schema Pivot',
    skills: ['Document Models', 'Aggregation', 'Indexes', 'Atlas'],
    color: '#00ed64',
    ovr: 86,
    z: -72,
  },
  {
    id: 'docker',
    title: 'Docker Core',
    move: 'Container Rush',
    skills: ['Images', 'Compose', 'Isolation', 'Reproducible Builds'],
    color: '#2496ed',
    ovr: 87,
    z: -82,
  },
  {
    id: 'azure',
    title: 'Azure Core',
    move: 'Cloud Clearance',
    skills: ['App Service', 'Functions', 'Storage', 'Deploy Pipelines'],
    color: '#0078d4',
    ovr: 88,
    z: -92,
  },
  {
    id: 'aws',
    title: 'AWS Core',
    move: 'Scale Burst',
    skills: ['Compute', 'Storage', 'Networking', 'Ops Basics'],
    color: '#ff9900',
    ovr: 87,
    z: -102,
  },
  {
    id: 'llms',
    title: 'AI Core',
    move: 'Vision Mode',
    skills: [
      'Generative AI',
      'Prompt Engineering',
      'Agentic AI',
      'LLMs',
    ],
    color: '#a855f7',
    ovr: 93,
    z: -112,
  },
  {
    id: 'ml',
    title: 'ML Core',
    move: 'Predictive Assist',
    skills: ['XGBoost', 'Scikit-learn', 'SHAP', 'Prediction Models'],
    color: '#f97316',
    ovr: 92,
    z: -122,
  },
  {
    id: 'rag',
    title: 'RAG Core',
    move: 'Context Lock',
    skills: ['Retrieval', 'Embeddings', 'Grounded Answers', 'Eval'],
    color: '#d9ff00',
    ovr: 91,
    z: -132,
  },
  {
    id: 'langchain',
    title: 'LangChain Core',
    move: 'Agent Chain',
    skills: ['Chains', 'Tools', 'Agents', 'Orchestration'],
    color: '#1c3c3c',
    ovr: 90,
    z: -140,
  },
];

export interface OpponentDef {
  id: string;
  label: string;
  z: number;
}

/** Every defender = a real engineering obstacle. Cap at 3 for quality. */
export const OPPONENTS: OpponentDef[] = [
  { id: 'legacy', label: 'Legacy Code', z: -28 },
  { id: 'bugs', label: 'Production Bugs', z: -72 },
  { id: 'interview', label: 'Technical Interviews', z: -118 },
];

/** @deprecated use OPPONENTS */
export const TACKLE_SPAWNS = OPPONENTS.map((o) => o.z);

/** Stadium advertising boards — résumé as sponsors. */
export const STADIUM_BOARDS = [
  'Leadgate',
  'MADAlgos',
  'CredNova',
  'AI Tutor',
  'LangChain',
  'Docker',
  'Next.js',
  'MongoDB',
  'Azure',
];

/** ACT1 tunnel wall milestones. */
export const TUNNEL_MILESTONES = [
  'FIRST LINE OF CODE',
  'FIRST DEPLOYMENT',
  'FIRST CLIENT',
  'FIRST INTERNSHIP',
  'FIRST HACKATHON',
  'FIRST PRODUCTION RELEASE',
];

export const TUNNEL_COMMITS = [
  'feat: ship auth flow',
  'fix: production crash',
  'perf: cut TTFB 40%',
  'feat: RAG pipeline',
  'chore: azure deploy',
  'docs: architecture',
];
